import React, { useEffect, useRef } from "react";

export type FundParseConsoleLineType = "status" | "stdout" | "stderr";

export interface FundParseConsoleLine {
    id: string;
    type: FundParseConsoleLineType;
    text: string;
}

interface FundParseJobConsoleProps {
    lines: FundParseConsoleLine[];
    isRunning: boolean;
}

function lineClassName(type: FundParseConsoleLineType): string {
    if (type === "status") {
        return "text-slate-400";
    }
    if (type === "stderr") {
        return "text-red-400";
    }
    return "text-green-300";
}

export function FundParseJobConsole({ lines, isRunning }: FundParseJobConsoleProps): React.ReactElement {
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (typeof bottomRef.current?.scrollIntoView === "function") {
            bottomRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [lines]);

    return (
        <div className="rounded-lg border border-gray-700 bg-slate-900 p-4">
            <div className="mb-2 flex items-center justify-between font-mono text-xs text-slate-400">
                <span>Job console</span>
                {isRunning ? <span className="animate-pulse text-amber-300">Running...</span> : null}
            </div>
            <pre
                aria-live="polite"
                className="max-h-96 overflow-auto font-mono text-xs whitespace-pre-wrap"
            >
                {lines.length === 0 ? (
                    <span className="text-slate-500">Run a job to see output here.</span>
                ) : (
                    lines.map((line) => (
                        <div key={line.id} className={lineClassName(line.type)}>
                            {line.text}
                        </div>
                    ))
                )}
                <div ref={bottomRef} />
            </pre>
        </div>
    );
}
